import { z } from 'zod';

import { CashOperationIdentityDtoSchema } from './cash-operation-identity.schema.js';
import {
  cashNeededRequestStatusValues,
  type CancelCashNeededRequest,
  type CashNeededRequestDto,
  type CashNeededRequestStatus,
  type CreateCashNeededRequest,
  type ListCashNeededRequestsRequest,
  type ListCashNeededRequestsResponse,
} from './cash-needed-requests.dto.js';

const timestampSchema = z.iso.datetime();

export const CashNeededRequestStatusSchema: z.ZodType<CashNeededRequestStatus> = z.enum(
  cashNeededRequestStatusValues,
);

export const CashNeededRequestDtoSchema: z.ZodType<CashNeededRequestDto> =
  CashOperationIdentityDtoSchema.extend({
    id: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    stationId: z.uuid(),
    cashLocationId: z.uuid(),
    cashSessionId: z.uuid(),
    requestedAmountMinor: z.number().int().positive(),
    note: z.string().trim().min(1).max(500).nullable(),
    status: CashNeededRequestStatusSchema,
    createdByUserId: z.uuid(),
    fulfilledByCashTransferId: z.uuid().nullable(),
    fulfilledAt: timestampSchema.nullable(),
    fulfilledByUserId: z.uuid().nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelledByUserId: z.uuid().nullable(),
    cancellationReason: z.string().trim().min(1).max(500).nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  }).superRefine((value, context) => {
    const hasFulfillment = Boolean(
      value.fulfilledByCashTransferId && value.fulfilledAt && value.fulfilledByUserId,
    );
    const hasCancellation = Boolean(value.cancelledAt && value.cancelledByUserId);
    if ((value.status === 'fulfilled') !== hasFulfillment) {
      context.addIssue({
        code: 'custom',
        path: ['fulfilledByCashTransferId'],
        message: 'Fulfillment evidence must exist only for fulfilled requests.',
      });
    }
    if ((value.status === 'cancelled') !== hasCancellation) {
      context.addIssue({
        code: 'custom',
        path: ['cancelledAt'],
        message: 'Cancellation evidence must exist only for cancelled requests.',
      });
    }
  });

export const CreateCashNeededRequestSchema: z.ZodType<CreateCashNeededRequest> = z.object({
  companyId: z.uuid(),
  operationId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  cashLocationId: z.uuid(),
  cashSessionId: z.uuid(),
  requestedAmountMinor: z.number().int().positive(),
  note: z.string().trim().min(1).max(500).nullable().optional(),
  occurredAt: timestampSchema.optional(),
});

export const CancelCashNeededRequestSchema: z.ZodType<CancelCashNeededRequest> = z.object({
  companyId: z.uuid(),
  operationId: z.uuid(),
  requestId: z.uuid(),
  reason: z.string().trim().min(1).max(500).nullable().optional(),
  occurredAt: timestampSchema.optional(),
});

export const ListCashNeededRequestsRequestSchema: z.ZodType<ListCashNeededRequestsRequest> =
  z.object({
    companyId: z.uuid(),
    locationId: z.uuid().optional(),
    cashLocationId: z.uuid().optional(),
    cashSessionId: z.uuid().optional(),
    status: CashNeededRequestStatusSchema.optional(),
  });

export const ListCashNeededRequestsResponseSchema: z.ZodType<ListCashNeededRequestsResponse> =
  z.object({ items: z.array(CashNeededRequestDtoSchema) });
